import { z } from 'zod';
import type { UserSourceCreateDTO } from '@api/DTO/User/UserSource/dto';
import type { UserSourceInitialValues } from './types';

import { buildDefaults, createFormModule, mapObject, valueToUndefined } from '@app/Utilities/Form';

/**
 * Zod schema validating UI-facing values for the User Source form.
 *
 * Notes:
 * - This schema validates form state only.
 * - API-level constraints are intentionally handled elsewhere.
 */
const schema = z.object({
  /**
   * Name of the user source.
   *
   * Always represented as a string in the form layer.
   * Empty values are handled later by the mapper or API validation.
   */
  sourceName: z.string(),
});

/**
 * Canonical base defaults for the User Source form.
 *
 * Rules:
 * - Must include every key in {@link UserSourceValues}
 * - Should remain immutable
 * - Represents initial UI state when no data is provided
 */
const baseDefaults: UserSourceValues = {
  /**
   * Empty string represents "no value" for controlled text inputs.
   */
  sourceName: '',
};

/**
 * User Source form module.
 *
 * This module defines the complete contract for the User Source form:
 * - schema: UI-facing validation
 * - baseDefaults: canonical default state
 * - defaults(): merges partial initial values into a complete form state
 * - mapper(): converts form values into an API DTO
 */
export const userSourceForm = createFormModule({
  schema,
  baseDefaults,

  /**
   * Builds a complete default value set for the form by merging partial
   * initial values over canonical base defaults.
   *
   * @param initial Partial initial values (e.g. from API or parent state)
   */
  defaults: (initial?: Partial<UserSourceInitialValues>) => buildDefaults(baseDefaults, initial),

  /**
   * Maps validated form values into an API-ready DTO.
   *
   * Responsibilities:
   * - Convert UI placeholders (e.g. empty strings) into `undefined`
   * - Produce a payload suitable for create semantics
   *
   * @param values Validated form values
   * @returns DTO suitable for submission to the create endpoint
   */
  mapper: (values): UserSourceCreateDTO =>
    mapObject(values, {
      sourceName: (v) => valueToUndefined(v.sourceName),
    }),
});

/**
 * UI-facing form values type inferred from the schema.
 *
 * Used by:
 * - Form components
 * - Default builders
 * - Mapper functions
 *
 * This type should never be used directly as an API payload.
 */
export type UserSourceValues = z.infer<typeof schema>;
